import React, {FC, ReactNode, useCallback, useLayoutEffect, useMemo, useState} from 'react';
import HtmlDialog from "../../ui/dialogs/HtmlDialog";
import TransactionCard from "../../Financial/Transactions/TransactionCard";
import type {EntityMetaTransforms } from "../../ui/EntityMeta/EntityMeta";
import EntityMeta from "../../ui/EntityMeta/EntityMeta";
import Transaction from "../../entities/transaction";
import util from "../../lib/util";
import {stripHTML} from "../../lib/picos/textUtils";
import i18n from "../../i18n/i18n";
import FinancialHelper from "../../Financial/FinancialHelper";
import FormatProcessorPaymentMeta from "../../Financial/PaymentProcessor/FormatProcessorPayment";
import ErrorBoundaryO from "../../ErrorBoundary/ErrorBoundaryO";
import type {RootState} from "../../@types/store";
import {connect} from "react-redux";
import {formatFMLFromId} from "../../Contacts/ContactFormat";
import CopyText from "../../ui/CopyText";
import {format} from "date-fns";
import newHRTimestamp from "../../lib/picos/timestamp";
import {getCurrentUserId} from "../../lib/picos/currentLogin";
import {Status, uuid} from "../../@types/entity";
import {useRootDispatch} from "../../hooks/useOutlawStore";
import {saveTransactionQueue} from "../../store/actions/transactionActions";
import EditableTable, {EditableTableProps, Justify} from "../../ui/EditableTable/EditableTable";
import Icons from "../../ui/IconButton/Icons";
import useComponentCache from "../../hooks/useComponentCache";
import {BAD_TRANSACTIONS_SORT_PROPS} from "./badTransactionsSortProps";

export type BadTransaction = {
    id: uuid,
    transaction: Transaction,
    date: string,
    relatedEntity: string,
    code: string,
    clients: string,
    type: string,
    amount: number,
    sourceType: string,
    targetType: string,
    reasonCode: string,
    reasonMessage: ReactNode
}

type BadTransactionState = {
    auditTransaction: Transaction | null,
}

const nameMap = {
    'txDate': 'Date',
    'txType': 'Type',
    'invoiceId': 'Invoice',
    'invoiceIds': 'Invoices',
    'matterIds': 'Cases',
    'clientIds': 'Clients',
    'processorPaymentMeta': 'Payment Processor'
};

const ignoreFields = [
    'importSource',
    'entityId',
    'entityType',
    'importId',
    'paymentDistribution',
];

const BadTransactions: FC<BadTransactionsProps> = props => {
    const {cacheSave, cacheValue, cacheReset} = useComponentCache<BadTransactionState>('bad-transactions');

    const dispatch = useRootDispatch();

    const [auditTransaction, setAuditTransaction] = useState<Transaction | null>(cacheValue('auditTransaction', null));

    useLayoutEffect(() => {
        return () => {
            cacheSave({auditTransaction});
        }
    }, [auditTransaction, cacheSave]);

    const onEdit = useCallback((tr: Transaction) => {
        const trClone = tr.clone();
        trClone.status = Status.Update;
        trClone.addedBy = getCurrentUserId();
        trClone.addedOn = newHRTimestamp();
        dispatch(saveTransactionQueue(trClone));
    }, [dispatch]);

    const txns: Transaction[] = useMemo(() => {
        return props.transactions
            .filter((tr: Transaction) => FinancialHelper.transactionFailureReason(tr) !== null)
            .filter((tr: Transaction) => tr.status !== 'D')
            .sort((a, b) => b.txDate.getTime() - a.txDate.getTime())
    }, [props.transactions]); // Do not change!

    const badTransactions: BadTransaction[] = useMemo(() => {
            if (props.transactions.length === 0 || props.contacts.length === 0) return [];

            return txns.map((tr: Transaction) => {
                    const reason = FinancialHelper.transactionFailureReason(tr)!;
                    let matterId = tr.matterIds[0] || tr.matterIds[1];
                    if (!matterId && tr.sourceType === 'M') matterId = tr.source;
                    if (!matterId && tr.targetType === 'M') matterId = tr.target;
                    const matter = matterId ? props.matters.get(matterId) : null;
                    const lead = props.leads.get(tr.matterIds[0] || tr.matterIds[1]);

                    let clientIds = [];
                    if (tr?.sourceType === 'C' && tr.source) clientIds = [tr.source];
                    else if (tr?.clientIds?.length > 0) clientIds = tr.clientIds;
                    else if (matter?.clients?.length > 0) clientIds = matter.clients;

                    const clientNames = clientIds
                        .map(cid => cid ? formatFMLFromId(cid) : null)
                        .filter(t => !!t)
                        .join(', ');

                    return {
                        id: tr.id,
                        transaction: tr,
                        date: format(tr.txDate, 'yyyy-MM-dd'),
                        relatedEntity: matter?.name || lead?.name || '[None]',
                        code: matter?.code || '[None]',
                        clients: clientNames,
                        type: FinancialHelper.decodeTransactionType(tr.txType),
                        amount: tr.amount,
                        sourceType: FinancialHelper.decodeTransactionCode(tr.sourceType),
                        targetType: FinancialHelper.decodeTransactionCode(tr.targetType),
                        reasonCode: reason.code,
                        reasonMessage: reason.message,
                    }
                })
        }, [props.leads, props.matters, props.contacts.length, props.transactions.length, txns]);

    const getBad = useCallback((tr: Transaction) => {
        return badTransactions.find(bt => bt.transaction.id === tr.id) ?? {
            id: tr.id,
            transaction: tr,
            date: '',
            relatedEntity: '',
            code: '',
            clients: '',
            type: '',
            amount: 0,
            sourceType: '',
            targetType: '',
            reasonCode: '',
            reasonMessage: '',
        } as BadTransaction;
    }, [badTransactions]);

    const renderTransactionId = useCallback((tr: Transaction) => {
        const bt = getBad(tr);
        return <CopyText useCodeStyle={true} text={bt.id || ''}/>
    }, [getBad]);

    const renderDate = useCallback((tr: Transaction) => {
        const bt = getBad(tr);
        return bt.date || '';
    }, [getBad]);

    const renderRelatedEntity = useCallback((tr: Transaction) => {
        const bt = getBad(tr);
        return bt.relatedEntity;
    }, [getBad]);

    const renderAmount = useCallback((tr: Transaction) => {
        const bt = getBad(tr);
        return util.formatCurrency(bt.amount, false);
    }, [getBad]);

    const renderCode = useCallback((tr: Transaction) => {
        const bt = getBad(tr);
        return bt.code;
    }, [getBad]);

    const renderClients = useCallback((tr: Transaction) => {
        const bt = getBad(tr);
        return bt.clients;
    }, [getBad]);

    const renderType = useCallback((tr: Transaction) => {
        const bt = getBad(tr);
        return bt.type;
    }, [getBad]);

    const renderSourceType = useCallback((tr: Transaction) => {
        const bt = getBad(tr);
        return bt.sourceType;
    }, [getBad]);

    const renderTargetType = useCallback((tr: Transaction) => {
        const bt = getBad(tr);
        return bt.targetType;
    }, [getBad]);

    const renderReasonCode = useCallback((tr: Transaction) => {
        const bt = getBad(tr);
        return bt.reasonCode;
    }, [getBad]);

    const renderReasonMessage = useCallback((tr: Transaction) => {
        const bt = getBad(tr);
        return bt.reasonMessage;
    }, [getBad]);

    const BadTransactionsTable = EditableTable as unknown as React.ComponentType<Partial<EditableTableProps<Transaction>>>;

    const properties = useMemo(() => [
        {label: 'transactionId', displayLabel: 'Transaction ID', width: '1fr', renderItemFn: renderTransactionId},
        {label: 'date', renderItemFn: renderDate},
        {label: 'relatedEntity', displayLabel: 'Lead or Case', width: '1fr', renderItemFn: renderRelatedEntity},
        {label: 'code', width: 'minmax(min-content, 1fr)', renderItemFn: renderCode},
        {label: 'clients', width: '1fr', renderItemFn: renderClients},
        {label: 'type', width: '1fr', renderItemFn: renderType},
        {label: 'amount', justify: Justify.RIGHT, renderItemFn: renderAmount},
        {label: 'sourceType', displayLabel: 'Source', width: '1fr', renderItemFn: renderSourceType},
        {label: 'targetType', displayLabel: 'Target', width: '1fr', renderItemFn: renderTargetType},
        {label: 'reasonCode', renderItemFn: renderReasonCode},
        {label: 'reasonMessage', width: '4fr', renderItemFn: renderReasonMessage},
    ], [renderAmount, renderClients, renderCode, renderDate, renderReasonCode, renderReasonMessage, renderRelatedEntity, renderSourceType, renderTargetType, renderTransactionId, renderType]);

    const otherActions = useMemo(() => [{
        label: 'Audit Log',
        selectedIcon: Icons.HISTORY,
        color: 'navy',
        action: (tr: Transaction) => setAuditTransaction(tr),
    }], []);

    const transforms: EntityMetaTransforms = useMemo(() => ({
        amount: util.formatCurrency,
        notes: notes => stripHTML(notes),
        txType: t => !!t ? i18n.ls('transaction', t) : '',
        sourceType: t => t === 'G' ? 'Client Trust' : t === 'M' ? 'Case Trust' : t === 'B' ? 'Operating Account' : t === 'C' ? 'Client/Contact' : t === 'E' ? 'Escrow' : t,
        targetType: t => t === 'G' ? 'Client Trust' : t === 'M' ? 'Case Trust' : t === 'B' ? 'Bank Account' : t === 'O' ? 'Operating Account' : t === 'C' ? 'Client/Contact' : t === 'E' ? 'Escrow' : t,
        source: s => FinancialHelper.getSourceTarget(s),
        target: s => FinancialHelper.getSourceTarget(s),
        processorPaymentMeta: FormatProcessorPaymentMeta,
        // matterIds: mids => JSON.stringify(mids), //mids?.map(mid => CaseHelper.getMatterFromId(mid)?.name).join(', '),
        // invoiceId: id => store.getState().invoices
    } as EntityMetaTransforms), []);

    return <ErrorBoundaryO uniqueKey='bad-transactions'>
            {(txns.length > 0) && <section>
                {/* Bad Transactions Table */}
                <div className='support-form__editable-table-container'>
                    <BadTransactionsTable name='Bad Transactions'
                                          entityName='transaction'
                                          urlQueryKey="bad-transaction"
                                          items={txns}
                                          editor={TransactionCard}
                                          properties={properties}
                                          sortProps={BAD_TRANSACTIONS_SORT_PROPS}
                                          onEdit={onEdit}
                                          hideGridOnEdit={true}
                                          otherActions={otherActions}
                    />
                </div>

                {/* Show Blame Log if selected */}
                {(props.misc.isConnected && auditTransaction) && <div>
					<HtmlDialog title='Edit Transaction' isShown={true} onClose={() => setAuditTransaction(null)}>
						<EntityMeta entityType="transaction"
						            entity={auditTransaction}
						            hidden={false}
						            showButton={false}
						            startOpen={true}
						            onVersionChange={(e) => setAuditTransaction(e as Transaction)}
						/>
					</HtmlDialog>
				</div>}
			</section>}
    </ErrorBoundaryO>;
};

export type BadTransactionsProps = Pick<RootState, 'contacts' | 'invoices' | 'leads' | 'matters' | 'transactions' | 'misc'>;

const mapStateToProps = (state: RootState) => ({
    contacts: state.contacts,
    invoices: state.invoices,
    leads: state.leads,
    matters: state.matters,
    transactions: state.transactions,
    misc: state.misc,
})

export default connect(mapStateToProps)(BadTransactions);